import { Copy } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";

type CopyButtonProps = {
  value: string;
  label?: string;
  size?: "sm" | "xs";
  stopPropagation?: boolean;
  onError?: (message: string) => void;
};

// Reusable copy button with transient "Copied!" feedback.
// Shared between the file list (FRE-268) and the file detail view (FRE-269).
export function CopyButton({
  value,
  label = "Copy URL",
  size = "sm",
  stopPropagation = false,
  onError,
}: CopyButtonProps) {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  async function handleClick(event: React.MouseEvent<HTMLButtonElement>) {
    if (stopPropagation) event.stopPropagation();
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      onError?.("Failed to copy — clipboard unavailable");
    }
  }

  return (
    <Button
      type="button"
      variant="outline"
      size={size}
      onClick={(e) => void handleClick(e)}
      aria-label={label}
    >
      <Copy data-icon="inline-start" />
      {copied ? "Copied!" : label}
    </Button>
  );
}
